import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import { day } from '../../lib/when';
import { useToast } from '../../components/ToastContext';
import useMediaQuery from '../../hooks/useMediaQuery';
import PageBar from '../../components/PageBar';
import { myWarehouseIds } from '../../lib/assignments';
import { STATE_COLOUR } from './parts';

/*
  My Exports: a rep takes their own counting away as a spreadsheet.

  Built to Group C's approved drawing on 5 September 2026. **It is called My
  Exports and not Reports**, which Group C settled on 31 August: "Reports"
  collided with the four admin reports, which are a different thing entirely.

  **Two files a rep could quietly get a short one of.** Both exports asked the
  database once and wrote whatever came back, and a request brings back about a
  thousand rows and says nothing about the rest. One rep's forty locations carry
  more than a thousand lines between them, so **"Exported 1,000 line items"
  could be printed on a file that was missing the rest of the quarter**, with
  nothing to say so. Both read a page at a time now.
*/

// How many location chips are shown before the rest go behind "+N more".
// Eight fills two rows on a desk and five fills the same space on a phone,
// which is what the drawings show at each size.
const SHOWN_DESK = 8;
const SHOWN_PHONE = 5;

function Eyebrow({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{children}</span>
      {right !== undefined && <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />}
      {right !== undefined && (
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{right}</span>
      )}
    </div>
  );
}

// A chip you can press. Used for both the count and the locations, because the
// drawing draws them the same way.
function Chip({ on, onClick, children, tick, dot }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 9,
        minHeight: 44, padding: '8px 16px', borderRadius: 'var(--oh-radius-control)',
        fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
        textAlign: 'left', border: 'none',
        color: on ? 'var(--action-text)' : 'var(--text-primary)',
        background: on ? 'var(--selected)' : 'var(--surface-glass)',
        boxShadow: 'inset 0 0 0 1px ' + (on ? 'var(--border)' : 'var(--border)'),
      }}>
      {tick && (
        on
          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" style={{ flexShrink: 0 }}><polyline points="4 13 9 18 20 6" /></svg>
          : <span style={{ width: 13, height: 13, borderRadius: 4, boxShadow: 'inset 0 0 0 1.5px var(--border-strong)', flexShrink: 0 }} />
      )}
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
      <span style={{ minWidth: 0 }}>{children}</span>
    </button>
  );
}

/*
  **Which sheets a person may export from a count**, the owner's answer of 18
  September 2026 (the audit after the rebrand, question 20): *"Reps should only
  ever get the counts they are responsible for or counted, managers would get
  access to their entire region."*

  - **A rep**: the sheets at the locations they cover now, and any sheet they
    held in that count, even at a location they have since stopped covering.
  - **A manager**: every sheet in the regions they cover.

  Until then both got only the locations they personally covered today, so a
  manager with no locations of their own was offered nothing at all (Charlie
  Cude, 0 of his region's 12 sheets in Q4 2026), and a rep who moved on lost
  the counts they had done. Proved on the live database by acting as each
  person, undone: the database lets each of them read what this offers.
*/
export async function placesFor(profile, cycleId) {
  const select = 'id, status, rep_id, warehouse:warehouses!inner(id, name, region_id)';
  if (profile?.role === 'manager') {
    const { data: regions, error } = await supabase.from('user_regions').select('region_id').eq('user_id', profile.id);
    if (error) return { data: [], error };
    const ids = (regions || []).map(r => r.region_id);
    if (!ids.length) return { data: [], error: null };
    return fetchAll(() => supabase.from('inventory_counts').select(select)
      .eq('cycle_id', cycleId).in('warehouse.region_id', ids).order('id'));
  }
  const mine = await myWarehouseIds(profile.id);
  const either = mine.length
    ? 'warehouse_id.in.(' + mine.join(',') + '),rep_id.eq.' + profile.id
    : 'rep_id.eq.' + profile.id;
  return fetchAll(() => supabase.from('inventory_counts').select(select)
    .eq('cycle_id', cycleId).or(either).order('id'));
}

export default function MyExports({ profile }) {
  const toast = useToast();
  const wide  = useMediaQuery('(min-width: 640px)');
  const [counts, setCounts]         = useState([]);   // the quarterly counts there have been
  const [chosenCount, setChosen]    = useState('');
  const [places, setPlaces]         = useState([]);   // this rep's sheets in that count
  const [picked, setPicked]         = useState([]);   // [] means all of them
  const [loadingPlaces, setLoading] = useState(false);
  const [showAll, setShowAll]       = useState(false);
  const [busy, setBusy]             = useState('');

  useEffect(() => {
    supabase.from('count_cycles').select('id, name').order('created_at', { ascending: false })
      .then(({ data }) => {
        setCounts(data || []);
        if (data?.length) setChosen(data[0].id);
      });
  }, []);

  useEffect(() => {
    if (!chosenCount || !profile?.id) return;
    let live = true;
    setLoading(true);
    setPicked([]);
    setShowAll(false);
    (async () => {
      const { data } = await placesFor(profile, chosenCount);
      if (!live) return;
      // **Alphabetical.** They came back in whatever order the database chose,
      // so a rep looking for one location among 35 chips had to read all 35.
      setPlaces((data || []).slice().sort((a, b) =>
        (a.warehouse?.name || '').localeCompare(b.warehouse?.name || '')));
      setLoading(false);
    })();
    return () => { live = false; };
  }, [chosenCount, profile]);

  function toggle(id) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  // Nothing chosen means all of them, which is what the drawing's count line
  // says and what this screen has always done.
  const forExport = picked.length === 0 ? places : places.filter(p => picked.includes(p.id));
  const countName = counts.find(c => c.id === chosenCount)?.name || 'export';

  function download(rows, name) {
    const csv  = rows.map(r => r.map(v => '"' + String(v ?? '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a    = document.createElement('a');
    a.href = url; a.download = name + '.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  async function exportSummary() {
    if (!forExport.length) { toast.warning('There is nothing to export.'); return; }
    setBusy('summary');
    const ids = forExport.map(p => p.id);
    const { data, error } = await fetchAll(() => supabase
      .from('inventory_counts')
      .select('id, status, submitted_at, approved_at, warehouse:warehouses(name, region:regions(name))')
      .in('id', ids)
      .order('id'));
    if (error) { toast.error('Could not export: ' + error.message); setBusy(''); return; }
    const rows = (data || []).map(r => [
      r.warehouse?.name || '',
      r.warehouse?.region?.name || '',
      r.status || '',
      r.submitted_at ? day(r.submitted_at) : '',
      r.approved_at ? day(r.approved_at) : '',
    ]);
    rows.sort((a, b) => a[0].localeCompare(b[0]));
    download([['Location', 'Region', 'Status', 'Submitted', 'Approved'], ...rows],
      'OnHand summary ' + countName);
    toast.success('Exported ' + rows.length + ' location' + (rows.length !== 1 ? 's' : ''));
    setBusy('');
  }

  async function exportEveryLine() {
    if (!forExport.length) { toast.warning('There is nothing to export.'); return; }
    setBusy('lines');
    const ids = forExport.map(p => p.id);
    /*
      **A page at a time, both of them.** This one asked once and wrote whatever
      came back, which for a rep with a quarter's counting behind them is the
      first thousand lines and no more, under a message saying how many were
      exported. The count was true about the file and false about the quarter.
    */
    const [{ data: lines, error }, { data: meta }] = await Promise.all([
      fetchAll(() => supabase
        .from('count_line_items')
        .select('inventory_count_id, item_number_raw, description_raw, vendor_raw, quantity, previous_quantity, not_in_catalog, is_new_item')
        .in('inventory_count_id', ids)
        .order('id')),
      fetchAll(() => supabase
        .from('inventory_counts')
        .select('id, status, submitted_at, warehouse:warehouses(name, region:regions(name))')
        .in('id', ids)
        .order('id')),
    ]);
    if (error) { toast.error('Could not export: ' + error.message); setBusy(''); return; }
    const by = {};
    for (const m of meta || []) by[m.id] = m;
    const rows = (lines || []).map(l => {
      const m = by[l.inventory_count_id];
      return [
        m?.warehouse?.name || '',
        m?.warehouse?.region?.name || '',
        l.item_number_raw || '',
        l.description_raw || '',
        l.vendor_raw || '',
        // **Blank is not zero**, rule 9, and it matters most in a spreadsheet:
        // a line nobody has been to must not arrive as a counted nought.
        l.quantity ?? '',
        l.previous_quantity ?? '',
        l.is_new_item ? 'Yes' : 'No',
        l.not_in_catalog ? 'Yes' : 'No',
        m?.submitted_at ? day(m.submitted_at) : '',
        m?.status || '',
      ];
    });
    rows.sort((a, b) => a[0].localeCompare(b[0]) || a[2].localeCompare(b[2]));
    download([['Location', 'Region', 'Item number', 'Description', 'Vendor', 'Quantity',
      'Counted last time', 'New item', 'Not in the catalog', 'Submitted', 'Status'], ...rows],
      'OnHand every line ' + countName);
    toast.success('Exported ' + rows.length + ' line' + (rows.length !== 1 ? 's' : ''));
    setBusy('');
  }

  const limit = wide ? SHOWN_DESK : SHOWN_PHONE;
  const visible = showAll ? places : places.slice(0, limit);
  const hidden  = places.length - visible.length;

  return (
    <>
      <PageBar title="My Exports" />
      <div className="card" style={{ padding: '20px 22px' }}>
        <Eyebrow>What to export</Eyebrow>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 22 }}>
          {counts.length === 0
            ? <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>There have been no counts yet.</span>
            : counts.map(c => (
              <Chip key={c.id} on={c.id === chosenCount} onClick={() => setChosen(c.id)}>{c.name}</Chip>
            ))}
        </div>

        <Eyebrow right={picked.length === 0
          ? 'All ' + places.length
          : picked.length + ' of ' + places.length + ' chosen'}>
          Locations
        </Eyebrow>
        {loadingPlaces ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0 20px' }}>Looking up your locations...</div>
        ) : places.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0 20px' }}>
            You had no locations in this count.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 22 }}>
            {visible.map(p => (
              <Chip key={p.id} tick dot={STATE_COLOUR[p.status] || 'var(--text-muted)'}
                on={picked.includes(p.id)} onClick={() => toggle(p.id)}>
                {p.warehouse?.name}
              </Chip>
            ))}
            {hidden > 0 && (
              <Chip onClick={() => setShowAll(true)}>
                <span style={{ color: 'var(--link)' }}>{'+' + hidden + ' more'}</span>
              </Chip>
            )}
            {showAll && places.length > limit && (
              <Chip onClick={() => setShowAll(false)}>
                <span style={{ color: 'var(--link)' }}>Show fewer</span>
              </Chip>
            )}
          </div>
        )}

        {/* Rule 33: the two acts this screen exists for are both out, and the
            more common one is the filled button. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button className="btn btn-primary" onClick={exportSummary} disabled={!!busy || !places.length}>
            {busy === 'summary' ? 'Exporting...' : 'Export a summary'}
          </button>
          {/* **`btn-quiet`, not `btn-outline`.** The outline button is white with
              a white ring, which reads as a raised pill on the grey canvas and
              vanishes on a white card. This card is white. */}
          <button className="btn btn-quiet" onClick={exportEveryLine} disabled={!!busy || !places.length}>
            {busy === 'lines' ? 'Exporting...' : 'Export every line'}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, marginTop: 12 }}>
          A summary is one row per location. Every line is one row per item counted.
          Choose no locations and you get all of them. Both open in Excel and Google Sheets.
        </div>
      </div>
    </>
  );
}
